import { NavLink, Outlet } from 'react-router-dom';
import { useMemo } from 'react';
import { useTheme } from '../theme/ThemeProvider';
import type { ThemeKey } from '../theme/themes';
import { useLocalization } from '../i18n/LocalizationProvider';
import type { LanguageKey } from '../i18n/translations';
import { useAuth } from '../auth/AuthContext';
import './Layout.css';

export default function Layout() {
  const { theme, themeOptions, setTheme } = useTheme();
  const { t, language, setLanguage, options: languageOptions } = useLocalization();
  const { session, logout } = useAuth();
  const currentYear = new Date().getFullYear();
  const navLinks = useMemo(() => {
    if (!session) {
      return [
        { to: '/', labelKey: 'layout.nav.home' },
        { to: '/login', labelKey: 'layout.nav.login' },
      ];
    }
    if (session.role === 'admin') {
      return [
        { to: '/', labelKey: 'layout.nav.home' },
        { to: '/admin', labelKey: 'layout.nav.admin' },
      ];
    }
    if (session.role === 'user') {
      return [
        { to: '/', labelKey: 'layout.nav.home' },
        { to: '/org', labelKey: 'layout.nav.org' },
        { to: '/dashboard', labelKey: 'layout.nav.dashboard' },
        { to: '/coupons', labelKey: 'layout.nav.coupons' },
        { to: '/present', labelKey: 'layout.nav.present' },
      ];
    }
    return [
      { to: '/', labelKey: 'layout.nav.home' },
      { to: '/subuser', labelKey: 'layout.nav.subuser' },
      { to: '/test', labelKey: 'layout.nav.profiles' },
    ];
  }, [session]);

  return (
    <div className="app-shell">
      <header className="app-header">
        <div className="header-inner">
          <div className="brand-block">
            <div className="brand-logo" aria-hidden>CT</div>
            <div>
              <p className="brand-name">{t('layout.brandName')}</p>
              <p className="brand-tagline">{t('layout.brandTagline')}</p>
            </div>
          </div>
          <nav className="nav-links" aria-label={t('layout.navLabel')}>
            {navLinks.map(link => (
              <NavLink
                key={link.to}
                to={link.to}
                className={({ isActive }) =>
                  isActive ? 'nav-link nav-link-active' : 'nav-link'
                }
                end={link.to === '/'}
              >
                {t(link.labelKey)}
              </NavLink>
            ))}
          </nav>
          <div className="header-controls">
            {session && (
              <div className="header-session">
                <button type="button" className="button-secondary" onClick={logout}>
                  {t('layout.logout')}
                </button>
              </div>
            )}
            <div className="control-group">
              <label htmlFor="theme-select" className="visually-hidden">
                {t('layout.themeLabel')}
              </label>
              <select
                id="theme-select"
                value={theme.key}
                onChange={event => setTheme(event.target.value as ThemeKey)}
                aria-label={t('layout.themeLabel')}
              >
                {themeOptions.map(option => (
                  <option key={option.key} value={option.key}>
                    {t(`layout.themeNames.${option.key}`)}
                  </option>
                ))}
              </select>
            </div>
            <div className="control-group">
              <label htmlFor="language-select" className="visually-hidden">
                {t('layout.languageLabel')}
              </label>
              <select
                id="language-select"
                value={language}
                onChange={event => setLanguage(event.target.value as LanguageKey)}
                aria-label={t('layout.languageLabel')}
              >
                {languageOptions.map(option => (
                  <option key={option.key} value={option.key}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </header>

      <main className="app-main">
        <Outlet />
      </main>

      <footer className="app-footer">
        <div className="footer-columns">
          <div>
            <h4>{t('layout.footer.contact')}</h4>
            <p>{t('layout.footer.email')}</p>
            <p>{t('layout.footer.phone')}</p>
          </div>
          <div>
            <h4>{t('layout.footer.company')}</h4>
            <ul>
              <li><a href="#about">{t('layout.footer.about')}</a></li>
              <li><a href="#security">{t('layout.footer.security')}</a></li>
              <li><a href="#support">{t('layout.footer.support')}</a></li>
            </ul>
          </div>
          <div>
            <h4>{t('layout.footer.resources')}</h4>
            <ul>
              <li><a href="#guides">{t('layout.footer.guides')}</a></li>
              <li><a href="#privacy">{t('layout.footer.privacy')}</a></li>
              <li><a href="#terms">{t('layout.footer.terms')}</a></li>
            </ul>
          </div>
        </div>
        <div className="footer-meta">
          <span>{t('layout.footer.copyright', { year: currentYear })}</span>
          <span>{t('layout.footer.metaTagline')}</span>
        </div>
      </footer>
    </div>
  );
}
